import React, { useMemo, useState, useEffect } from "react";
import { Play } from "lucide-react";

type MediaType = "image" | "gif" | "video";

interface GalleryItem {
  type: MediaType;
  src: string;
  thumbnail?: string;
}

interface ProductImageGalleryProps {
  product: any; // Using any ensures processing both normalized custom structures or backend types safely
  className?: string;
  layout?: "bottom" | "left";
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  product,
  className = "",
  layout = "bottom",
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const getMediaType = (url: string): MediaType => {
    if (!url) return "image";
    const cleanUrl = url.split("?")[0].toLowerCase();

    if (
      cleanUrl.endsWith(".mp4") ||
      cleanUrl.endsWith(".webm") ||
      cleanUrl.endsWith(".ogg") ||
      cleanUrl.includes("/video/") ||
      cleanUrl.includes("/videos/")
    ) {
      return "video";
    }
    if (cleanUrl.endsWith(".gif")) {
      return "gif";
    }
    return "image";
  };

  const galleryItems: GalleryItem[] = useMemo(() => {
    const items: GalleryItem[] = [];

    // 1. Process direct product images array from API response
    if (
      product?.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      product.images.forEach((image: string) => {
        if (image) {
          items.push({
            type: getMediaType(image),
            src: image,
          });
        }
      });
    }
    // 2. Fallback to single string product image
    else if (
      product?.productimage &&
      typeof product.productimage === "string"
    ) {
      items.push({
        type: getMediaType(product.productimage),
        src: product.productimage,
      });
    }

    // 3. Process extra subimages array if provided
    if (product?.subimage && Array.isArray(product.subimage)) {
      product.subimage.forEach((sub: any) => {
        const url = typeof sub === "string" ? sub : sub?.image;
        if (url && !items.some((i) => i.src === url)) {
          items.push({
            type: getMediaType(url),
            src: url,
          });
        }
      });
    }

    // 4. INCLUDE PRODUCT VIDEO URL (API Property: videoUrl)
    if (
      product?.videoUrl &&
      typeof product.videoUrl === "string" &&
      product.videoUrl.trim() !== ""
    ) {
      if (!items.some((i) => i.src === product.videoUrl)) {
        items.push({
          type: "video",
          src: product.videoUrl,
        });
      }
    }

    // 5. INCLUDE VIDEO FALLBACK ARRAY (Local Property: video)
    if (product?.video && Array.isArray(product.video)) {
      product.video.forEach((v: any) => {
        const url = typeof v === "string" ? v : v?.url || v?.src;
        if (url && !items.some((i) => i.src === url)) {
          items.push({
            type: "video",
            src: url,
          });
        }
      });
    }

    return items;
  }, [product]);

  // FIX: Reset selection ONLY when Product ID changes (not on state re-renders like timer ticks)
  const targetProductId =
    product?.productId || product?.productid || product?.id;

  useEffect(() => {
    setSelectedIndex(0);
    setZoomed(false);
  }, [targetProductId]);

  if (!galleryItems.length) {
    return (
      <div className="h-[450px] w-full flex items-center justify-center border border-dashed border-gray-300 rounded-xl bg-gray-50 text-gray-400 text-sm">
        No preview available
      </div>
    );
  }

  const selectedItem = galleryItems[selectedIndex] || galleryItems[0];

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setZoomed(false);
  };

  const Thumbnail = ({
    item,
    index,
    sizeClass,
  }: {
    item: GalleryItem;
    index: number;
    sizeClass: string;
  }) => (
    <button
      type="button"
      onClick={() => handleSelect(index)}
      className={`relative overflow-hidden rounded-lg border bg-white transition-all duration-200 flex-shrink-0 ${
        selectedIndex === index
          ? "border-blue-600 ring-1 ring-blue-600 scale-105"
          : "border-gray-300 hover:border-gray-500 opacity-80 hover:opacity-100"
      } ${sizeClass}`}
    >
      {item.type === "video" ? (
        <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
          <video src={item.src} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="rounded-full bg-white p-1.5 shadow-md">
              <Play size={12} className="ml-[1px] fill-black text-black" />
            </div>
          </div>
        </div>
      ) : (
        <img
          src={item.thumbnail || item.src}
          alt={`thumb-${index}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              "https://placehold.co/100x100?text=No+Image";
          }}
        />
      )}

      {item.type === "gif" && (
        <div className="absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5 text-[8px] font-semibold text-white">
          GIF
        </div>
      )}
    </button>
  );

  const renderMedia = () => {
    if (!selectedItem) return null;

    switch (selectedItem.type) {
      case "video":
        return (
          <video
            key={selectedItem.src}
            controls
            autoPlay
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-contain max-h-[450px] bg-black/90 rounded-xl"
          >
            <source src={selectedItem.src} />
            Your browser does not support video.
          </video>
        );

      case "gif":
        return (
          <img
            src={selectedItem.src}
            alt="gif"
            className="w-full h-full object-contain"
          />
        );

      default:
        return (
          <img
            src={selectedItem.src}
            alt="product"
            onClick={() => setZoomed((prev) => !prev)}
            className={`w-full h-full object-contain p-4 transition-transform duration-300 ease-in-out ${
              zoomed ? "scale-150 cursor-zoom-out" : "scale-100 cursor-zoom-in"
            }`}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://placehold.co/450x450?text=Image+Not+Found";
            }}
          />
        );
    }
  };

  return (
    <div
      className={`w-full ${
        layout === "left" ? "flex gap-4" : "flex flex-col"
      } ${className}`}
    >
      {/* LEFT THUMBNAILS CONTAINER */}
      {layout === "left" && (
        <div className="flex flex-col gap-3 overflow-y-auto max-h-[450px] pr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-200">
          {galleryItems.map((item, index) => (
            <Thumbnail
              key={`${item.src}-${index}`}
              item={item}
              index={index}
              sizeClass="w-[60px] h-[60px]"
            />
          ))}
        </div>
      )}

      {/* MAIN DISPLAY CONTAINER */}
      <div className="h-[450px] flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white relative flex items-center justify-center">
        {renderMedia()}
      </div>

      {/* BOTTOM THUMBNAILS CONTAINER */}
      {layout === "bottom" && (
        <div className="flex gap-3 overflow-x-auto pt-4 pb-1 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-gray-200">
          {galleryItems.map((item, index) => (
            <Thumbnail
              key={`${item.src}-${index}`}
              item={item}
              index={index}
              sizeClass="w-[60px] h-[60px]"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageGallery;